import {
  Directive,
  ElementRef,
  Renderer2,
  HostListener,
  Input,
  AfterViewInit
} from '@angular/core';

/**
 * usZipcodeFormat
 *
 * - Validates a U.S. ZIP code format:
 *    5 digits (e.g., "12345")
 *       or
 *    5+4 digits with a dash (e.g., "12345-6789").
 * - Auto-inserts a dash if the user types 9 digits in total (e.g. "123456789" becomes "12345-6789").
 * - Optionally enforces a max length of 10 characters (5 + dash + 4).
 *
 * Usage in template:
 *   <input matInput usZipcodeFormat [(ngModel)]="billingZip" />
 *
 * Additional ideas:
 *  - Combine with a custom Angular validator if you want to mark the field as invalid
 *    in a reactive form. Or just rely on the final string pattern test.
 */
@Directive({
  selector: '[usZipcodeFormat]',
  standalone: true
})
export class UsZipcodeFormatDirective implements AfterViewInit {
  /**
   * If true, we'll set `maxlength="10"` on the input
   * so the user cannot type beyond "12345-6789".
   */
  @Input() enforceMaxLength: boolean = true;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    if (this.enforceMaxLength) {
      this.renderer.setAttribute(this.el.nativeElement, 'maxlength', '10');
    }
  }

  /**
   * On input, we:
   * 1) Strip out non-digits and dash
   * 2) If we detect 9 digits, we auto-insert a dash after the first 5
   * 3) Ensure we only keep up to 10 characters total
   */
  @HostListener('input', ['$event.target.value'])
  onInput(value: string): void {
    // Strip everything but digits and dash
    let sanitized = value.replace(/[^\d-]/g, '');

    // Remove extra dash(es) if user typed them
    // (only allow 1 dash after the first 5 digits)
    // A naive approach: strip all dashes, re-insert if length is 9
    const digitsOnly = sanitized.replace(/-/g, '');

    // If we have 9 digits, let's auto-insert the dash after 5
    if (digitsOnly.length === 9) {
      sanitized = digitsOnly.substring(0, 5) + '-' + digitsOnly.substring(5, 9);
    } else {
      // Otherwise, it's just the digits so far
      sanitized = digitsOnly;
    }

    // If enforceMaxLength is true, slice to max 10
    if (this.enforceMaxLength) {
      sanitized = sanitized.slice(0, 10);
    }

    this.renderer.setProperty(this.el.nativeElement, 'value', sanitized);

    // Move cursor to end
    const len = sanitized.length;
    this.el.nativeElement.setSelectionRange(len, len);
  }
}
